import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, X, Star, ArrowUp, ArrowDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { formatRupiah, absUrl } from "@/lib/helpers";

const BADGES = ["Terbaru", "Best Seller", "Promo"];
const STOCKS = [{ v: "tersedia", l: "Tersedia" }, { v: "terbatas", l: "Stok Terbatas" }, { v: "habis", l: "Habis" }];

const EMPTY = {
  name: "", code: "", category_id: "", description: "", price: 0, promo_price: null,
  stock_status: "tersedia", badges: [], featured: false, active: true, archived: false,
  images: [], compatible_model_ids: [],
};

export default function ProductForm({ open, onClose, product, categories, brands, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [modelsByBrand, setModelsByBrand] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(product ? {
        name: product.name, code: product.code || "", category_id: product.category_id || "",
        description: product.description || "", price: product.price || 0, promo_price: product.promo_price,
        stock_status: product.stock_status || "tersedia", badges: product.badges || [],
        featured: !!product.featured, active: product.active !== false, archived: !!product.archived,
        images: product.images || [], compatible_model_ids: product.compatible_model_ids || [],
      } : EMPTY);
    }
  }, [open, product]);

  useEffect(() => {
    brands.forEach((b) => {
      if (!modelsByBrand[b.id]) api.get("/phone-models", { params: { brand_id: b.id, all: true } }).then((r) => setModelsByBrand((s) => ({ ...s, [b.id]: r.data }))).catch(() => {});
    });
  }, [brands]); // eslint-disable-line

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => {
        const imgs = [...f.images, { url: data.url, alt: f.name, is_primary: f.images.length === 0, order: f.images.length }];
        return { ...f, images: imgs };
      });
      toast.success("Gambar diunggah");
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal mengunggah"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const setPrimary = (i) => set({ images: form.images.map((im, idx) => ({ ...im, is_primary: idx === i })) });
  const removeImg = (i) => set({ images: form.images.filter((_, idx) => idx !== i) });
  const moveImg = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.images.length) return;
    const arr = [...form.images];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set({ images: arr.map((im, idx) => ({ ...im, order: idx })) });
  };

  const toggleBadge = (b) => set({ badges: form.badges.includes(b) ? form.badges.filter((x) => x !== b) : [...form.badges, b] });
  const toggleModel = (id) => set({ compatible_model_ids: form.compatible_model_ids.includes(id) ? form.compatible_model_ids.filter((x) => x !== id) : [...form.compatible_model_ids, id] });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    if (!form.category_id) return toast.error("Pilih kategori");
    setBusy(true);
    const payload = { ...form, price: Number(form.price), promo_price: form.promo_price ? Number(form.promo_price) : null };
    try {
      if (product) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produk disimpan");
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  const primaryImg = form.images.find((i) => i.is_primary) || form.images[0];
  const hasPromo = form.promo_price && form.promo_price < form.price;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{product ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>

        <div className="grid gap-5 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <div><Label>Nama Produk</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => set({ name: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kode Produk</Label><Input value={form.code} onChange={(e) => set({ code: e.target.value })} placeholder="otomatis" className="mt-1" /></div>
              <div>
                <Label>Kategori</Label>
                <Select value={form.category_id} onValueChange={(v) => set({ category_id: v })}>
                  <SelectTrigger data-testid="product-category-select" className="mt-1"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Harga Normal</Label><Input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => set({ price: e.target.value })} className="mt-1" /></div>
              <div><Label>Harga Promo (opsional)</Label><Input type="number" value={form.promo_price ?? ""} onChange={(e) => set({ promo_price: e.target.value })} className="mt-1" /></div>
            </div>
            <div>
              <Label>Status Stok</Label>
              <Select value={form.stock_status} onValueChange={(v) => set({ stock_status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{STOCKS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={3} className="mt-1" /></div>

            <div>
              <Label>Badge</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {BADGES.map((b) => (
                  <button key={b} type="button" onClick={() => toggleBadge(b)} className={`rounded-full border px-3 py-1 text-xs font-medium ${form.badges.includes(b) ? "border-accent bg-accent text-accent-foreground" : "border-border"}`}>{b}</button>
                ))}
              </div>
            </div>

            <div>
              <Label>Gambar Produk</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {form.images.map((im, i) => (
                  <div key={i} className="relative">
                    <img src={absUrl(im.url)} alt={im.alt} className={`h-20 w-20 rounded-xl object-cover ring-2 ${im.is_primary ? "ring-accent" : "ring-transparent"}`} />
                    <button type="button" onClick={() => removeImg(i)} className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white"><X className="h-3 w-3" /></button>
                    <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-0.5 bg-black/40 rounded-b-xl py-0.5">
                      <button type="button" onClick={() => setPrimary(i)} title="Utama"><Star className={`h-3 w-3 ${im.is_primary ? "fill-amber-400 text-amber-400" : "text-white"}`} /></button>
                      <button type="button" onClick={() => moveImg(i, -1)}><ArrowUp className="h-3 w-3 text-white" /></button>
                      <button type="button" onClick={() => moveImg(i, 1)}><ArrowDown className="h-3 w-3 text-white" /></button>
                    </div>
                  </div>
                ))}
                <label data-testid="product-image-upload" className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border text-muted-foreground hover:bg-secondary">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                  <span className="text-[10px]">Unggah</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} disabled={uploading} />
                </label>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Klik bintang untuk gambar utama. Panah untuk urutan.</p>
            </div>

            <div>
              <Label>Kompatibel dengan Tipe HP</Label>
              <div className="mt-1.5 space-y-2 rounded-xl border border-border p-3 max-h-52 overflow-y-auto">
                {brands.map((b) => (
                  <div key={b.id}>
                    <p className="text-xs font-semibold text-muted-foreground">{b.name}</p>
                    <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {(modelsByBrand[b.id] || []).map((m) => (
                        <label key={m.id} className="flex items-center gap-2 text-sm">
                          <Checkbox className="rounded-[4px]" checked={form.compatible_model_ids.includes(m.id)} onCheckedChange={() => toggleModel(m.id)} data-testid={`compat-${m.id}`} />
                          {m.name}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar: toggles + live preview */}
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-border p-3"><Label>Produk Unggulan</Label><Switch data-testid="product-featured-switch" checked={form.featured} onCheckedChange={(v) => set({ featured: v })} /></div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3"><Label>Aktif (tampil di toko)</Label><Switch checked={form.active} onCheckedChange={(v) => set({ active: v })} /></div>

            <div className="rounded-xl border border-border p-3">
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Preview</p>
              <div className="overflow-hidden rounded-xl border border-border">
                <div className="aspect-square bg-secondary">{primaryImg ? <img src={absUrl(primaryImg.url)} alt="preview" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-muted-foreground">Belum ada gambar</div>}</div>
                <div className="p-2">
                  <p className="text-sm font-semibold line-clamp-1">{form.name || "Nama Produk"}</p>
                  <p className="text-sm font-bold">{formatRupiah(hasPromo ? form.promo_price : form.price)} {hasPromo && <span className="text-xs text-muted-foreground line-through">{formatRupiah(form.price)}</span>}</p>
                </div>
              </div>
            </div>

            <button data-testid="save-product-btn" onClick={save} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Simpan Produk
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
